import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { OptionCard } from 'components/common/OptionCards';
import { TransportDropdownService } from 'service/dropdownServices/TransportDropdown';

interface TransportDictionaries {
  /** Вид транспорта: полуприцеп / грузовик / сцепка */
  kinds: OptionCard[];
  /** Тип кузова */
  bodyTypes: OptionCard[];
  /** Способ загрузки */
  loadingTypes: OptionCard[];
}

const EMPTY: TransportDictionaries = { kinds: [], bodyTypes: [], loadingTypes: [] };

const DictionariesContext = createContext<TransportDictionaries>(EMPTY);

/** Справочники грузятся один раз на всю форму — шагам и подписям чипов нужны одни и те же названия. */
export const TransportDictionariesProvider = ({ children }: { children: ReactNode }) => {
  const lang = useSelector((state: any) => state.settings.lang);
  const [kinds, setKinds] = useState<OptionCard[]>([]);
  const [bodyTypes, setBodyTypes] = useState<OptionCard[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<OptionCard[]>([]);

  useEffect(() => {
    const toOptions = (res: any): OptionCard[] =>
      res.data.map((el: any) => ({ value: el.id, label: el.value }));

    TransportDropdownService.getBodyTypesAdditional(lang)
      .then((res: any) => setKinds(toOptions(res)))
      .catch((e: any) => console.log(e.response));

    TransportDropdownService.getBodyTypes(lang)
      .then((res: any) =>
        setBodyTypes(toOptions(res).sort((a, b) => a.label.localeCompare(b.label))),
      )
      .catch((e: any) => console.log(e.response));

    TransportDropdownService.getLoadingTypes(lang)
      .then((res: any) => setLoadingTypes(toOptions(res)))
      .catch((e: any) => console.log(e.response));
  }, [lang]);

  const value = useMemo(
    () => ({ kinds, bodyTypes, loadingTypes }),
    [kinds, bodyTypes, loadingTypes],
  );

  return <DictionariesContext.Provider value={value}>{children}</DictionariesContext.Provider>;
};

export const useTransportDictionaries = () => useContext(DictionariesContext);

/**
 * Значение поля множественного выбора всегда как массив: в модели транспорта
 * `transports_loading_types` инициализируется пустой строкой, а с бэкенда приходит массив.
 */
export const toSelectedArray = (value: any): Array<string | number> => {
  if (Array.isArray(value)) return value;
  if (value === '' || value === null || value === undefined) return [];
  return [value];
};

/** Названия выбранных значений — для подписей под названием шага. */
export const labelsFor = (options: OptionCard[], selected: any) => {
  const values = toSelectedArray(selected);
  return options
    .filter((option) => values.some((item) => String(item) === String(option.value)))
    .map((option) => option.label);
};
